import "./passwordgenerator.css";
import { Fragment, useEffect, useMemo, useState } from "react";
import Helmet from "react-helmet";
import TinyTitle from "../../components/tinytitle/TinyTitle";

const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";
const NUMBERS = "0123456789";
const SYMBOLS = "!@#$%^&*()-_=+[]{};:,.<>?";

const getRandomInt = (max) => {
    if (!max || max <= 0) return 0;

    if (window.crypto && window.crypto.getRandomValues) {
        const array = new Uint32Array(1);
        window.crypto.getRandomValues(array);
        return array[0] % max;
    }

    return Math.floor(Math.random() * max);
};

const getRandomChar = (characters) => {
    return characters.charAt(getRandomInt(characters.length));
};

const shuffleString = (value) => {
    const arr = value.split("");

    for (let i = arr.length - 1; i > 0; i--) {
        const j = getRandomInt(i + 1);
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }

    return arr.join("");
};

const generatePassword = ({ length, uppercase, lowercase, numbers, symbols }) => {
    let selectedSets = [];
    let password = "";

    if (uppercase) selectedSets.push(UPPERCASE);
    if (lowercase) selectedSets.push(LOWERCASE);
    if (numbers) selectedSets.push(NUMBERS);
    if (symbols) selectedSets.push(SYMBOLS);

    if (selectedSets.length === 0) {
        return "";
    }

    selectedSets.forEach((set) => {
        password += getRandomChar(set);
    });

    const allCharacters = selectedSets.join("");

    while (password.length < length) {
        password += getRandomChar(allCharacters);
    }

    return shuffleString(password.slice(0, length));
};

const calculateStrength = (password, selectedCount) => {
    if (!password) {
        return {
            label: "Weak",
            percent: 10
        };
    }

    let score = 0;

    if (password.length >= 8) score += 20;
    if (password.length >= 12) score += 20;
    if (password.length >= 16) score += 15;
    if (selectedCount >= 2) score += 15;
    if (selectedCount >= 3) score += 15;
    if (selectedCount >= 4) score += 15;

    if (score > 100) score = 100;

    let label = "Weak";

    if (score >= 80) {
        label = "Strong";
    } else if (score >= 50) {
        label = "Medium";
    }

    return {
        label,
        percent: score
    };
};

const PasswordGenerator = () => {
    const [length, setLength] = useState(20);
    const [uppercase, setUppercase] = useState(true);
    const [lowercase, setLowercase] = useState(true);
    const [numbers, setNumbers] = useState(true);
    const [symbols, setSymbols] = useState(true);
    const [password, setPassword] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setTimeout(() => window.scrollTo(0, 0), 500);
    }, []);

    const totalSelected = [uppercase, lowercase, numbers, symbols].filter(Boolean).length;

    const regeneratePassword = (
        nextLength = length,
        nextUppercase = uppercase,
        nextLowercase = lowercase,
        nextNumbers = numbers,
        nextSymbols = symbols
    ) => {
        const total = [nextUppercase, nextLowercase, nextNumbers, nextSymbols].filter(Boolean).length;

        if (total === 0) {
            return;
        }

        const generatedPassword = generatePassword({
            length: nextLength,
            uppercase: nextUppercase,
            lowercase: nextLowercase,
            numbers: nextNumbers,
            symbols: nextSymbols
        });

        setPassword(generatedPassword);
        setCopied(false);
    };

    useEffect(() => {
        if (totalSelected > 0) {
            regeneratePassword();
        }
    }, []);

    const strength = useMemo(() => {
        return calculateStrength(password, totalSelected);
    }, [password, totalSelected]);

    const handleLengthChange = (e) => {
        const newLength = Number(e.target.value);
        setLength(newLength);
        regeneratePassword(newLength, uppercase, lowercase, numbers, symbols);
    };

    const handleToggleChange = (type, checked) => {
        let nextUppercase = uppercase;
        let nextLowercase = lowercase;
        let nextNumbers = numbers;
        let nextSymbols = symbols;

        if (type === "uppercase") nextUppercase = checked;
        if (type === "lowercase") nextLowercase = checked;
        if (type === "numbers") nextNumbers = checked;
        if (type === "symbols") nextSymbols = checked;

        const total = [nextUppercase, nextLowercase, nextNumbers, nextSymbols].filter(Boolean).length;

        if (total === 0) {
            return;
        }

        setUppercase(nextUppercase);
        setLowercase(nextLowercase);
        setNumbers(nextNumbers);
        setSymbols(nextSymbols);

        regeneratePassword(length, nextUppercase, nextLowercase, nextNumbers, nextSymbols);
    };

    const handleCopyPassword = async () => {
        if (!password) return;

        try {
            await navigator.clipboard.writeText(password);
            setCopied(true);

            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <Fragment>
            <Helmet>
                <title>Create Strong Random Passwords | SharePass</title>
                <meta
                    name="description"
                    content="Generate strong random passwords instantly in your browser. Customize length, uppercase, lowercase, numbers, and symbols with SharePass."
                />
                <meta
                    property="og:title"
                    content="Create Strong Random Passwords | SharePass"
                />
                <meta
                    property="og:description"
                    content="Generate strong random passwords instantly in your browser. Customize length, uppercase, lowercase, numbers, and symbols with SharePass."
                />
            </Helmet>

            <section className="password_generator_page">
                <div className="content_sec">
                    <div className="password_generator_wrap">
                        <div className="password_generator_heading">
                        <TinyTitle title="Create Strong Random Passwords" />
                            <h1>Password Generator</h1>
                            <p className="password_generator_intro">
                                Generate strong random passwords instantly in your browser. Customize length, uppercase, lowercase, numbers, and symbols with SharePass.
                            </p>
                        </div>

                        <div className="password_result_box">
                            <div className="password_result_main">
                                <div className="password_result_text">
                                    {password}
                                </div>

                                <div className="password_strength_wrap">
                                    <div className={`password_strength_bar ${strength.label.toLowerCase()}`}>
                                        <span style={{ width: `${strength.percent}%` }}></span>
                                    </div>
                                    <div className={`password_strength_label ${strength.label.toLowerCase()}`}>
                                        {strength.label}
                                    </div>
                                </div>
                            </div>

                            <div className="password_result_actions">
                                <button
                                    className="password_icon_button"
                                    onClick={() => regeneratePassword()}
                                    type="button"
                                    aria-label="Refresh password"
                                    title="Refresh password"
                                >
                                    ↻
                                </button>

                                <button
                                    className={`password_copy_button ${copied ? "copied" : ""}`}
                                    onClick={handleCopyPassword}
                                    type="button"
                                >
                                    {copied ? "Copied!" : "Copy"}
                                </button>
                            </div>
                        </div>

                        <div className="password_controls_box">
                            <div className="password_control_row">
                                <div className="password_control_title">
                                    Length: {length}
                                </div>

                                <div className="password_control_input">
                                    <input
                                        type="range"
                                        min="6"
                                        max="64"
                                        value={length}
                                        onChange={handleLengthChange}
                                    />
                                </div>
                            </div>

                            <div className="password_control_row password_control_row_toggle">
                                <div className="password_control_title">
                                    Include:
                                </div>

                                <div className="password_toggle_group">
                                    <label className="password_toggle_item">
                                        <button
                                            type="button"
                                            className={`toggle_switch ${uppercase ? "active" : ""}`}
                                            onClick={() => handleToggleChange("uppercase", !uppercase)}
                                            aria-label="Toggle uppercase"
                                        >
                                            <span className="toggle_switch_knob"></span>
                                        </button>
                                        <span className="password_toggle_text">Uppercase</span>
                                    </label>

                                    <label className="password_toggle_item">
                                        <button
                                            type="button"
                                            className={`toggle_switch ${lowercase ? "active" : ""}`}
                                            onClick={() => handleToggleChange("lowercase", !lowercase)}
                                            aria-label="Toggle lowercase"
                                        >
                                            <span className="toggle_switch_knob"></span>
                                        </button>
                                        <span className="password_toggle_text">Lowercase</span>
                                    </label>

                                    <label className="password_toggle_item">
                                        <button
                                            type="button"
                                            className={`toggle_switch ${numbers ? "active" : ""}`}
                                            onClick={() => handleToggleChange("numbers", !numbers)}
                                            aria-label="Toggle numbers"
                                        >
                                            <span className="toggle_switch_knob"></span>
                                        </button>
                                        <span className="password_toggle_text">Numbers</span>
                                    </label>

                                    <label className="password_toggle_item">
                                        <button
                                            type="button"
                                            className={`toggle_switch ${symbols ? "active" : ""}`}
                                            onClick={() => handleToggleChange("symbols", !symbols)}
                                            aria-label="Toggle symbols"
                                        >
                                            <span className="toggle_switch_knob"></span>
                                        </button>
                                        <span className="password_toggle_text">Symbols</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="password_generator_content">
                            <div className="password_generator_content_grid">
                                <div className="password_generator_content_card">
                                    <h2>What makes a password strong?</h2>
                                    <p>
                                        A strong password is long, unique, and hard to guess. The best passwords use a mix
                                        of uppercase letters, lowercase letters, numbers, and symbols, while avoiding common
                                        words, names, dates, and repeated patterns.
                                    </p>
                                </div>

                                <div className="password_generator_content_card">
                                    <h2>Why use a random password generator?</h2>
                                    <p>
                                        Random passwords are much harder to predict than passwords created manually. Using a
                                        password generator helps reduce the risk of weak, reused, or easy-to-guess passwords
                                        across your accounts.
                                    </p>
                                </div>

                                <div className="password_generator_content_card">
                                    <h2>How long should a password be?</h2>
                                    <p>
                                        Longer passwords are usually stronger. A good starting point is at least 12
                                        characters, while 16 or more characters offers stronger protection for important
                                        accounts and sensitive information.
                                    </p>
                                </div>

                                <div className="password_generator_content_card">
                                    <h2>Are generated passwords stored?</h2>
                                    <p>
                                        No. This password generator creates passwords directly in your browser. Your generated
                                        password is not sent anywhere, helping you keep full control over the result.
                                    </p>
                                </div>
                            </div>

                            <div className="password_generator_tips">
                                <h2>Tips for safer passwords</h2>
                                <div className="password_generator_tips_grid">
                                    <div className="password_generator_tip">
                                        <h3>Use a unique password for every account</h3>
                                        <p>
                                            Reusing passwords increases the risk that one leaked account can expose many
                                            others.
                                        </p>
                                    </div>

                                    <div className="password_generator_tip">
                                        <h3>Make important accounts longer</h3>
                                        <p>
                                            Email, banking, and work accounts should use especially long and unpredictable
                                            passwords.
                                        </p>
                                    </div>

                                    <div className="password_generator_tip">
                                        <h3>Enable multi-factor authentication</h3>
                                        <p>
                                            A strong password is even better when combined with an extra layer of protection.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Fragment>
    );
};

export default PasswordGenerator;